import DownloadIconButton from "$/components/DownloadIconButton";
import PricingModal from "$/components/PricingModal";
import { useAuth } from "$/util/auth";
import { getCreationBucket, getImagePath } from "$/util/creation_util";
import constants from "@blackink/core/categoryConstants";
import { Lock } from "@mui/icons-material";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import analytics from "../util/analytics";
import { createEvent, getImageUrl } from "../util/db";
import DownloadButton from "./DownloadButton";
import FavoriteButton from "./FavoriteButton";
import ProBadge from "./icons/ProBadge";

declare module "react" {
  interface ImgHTMLAttributes<T> extends HTMLAttributes<T> {
    fetchpriority?: "high" | "low" | "auto";
  }
}

function VisualizationButton({ onVisualizeClick }) {
  return (
    <>
      <IconButton
        aria-label="visualize"
        color="default"
        onClick={onVisualizeClick}
        sx={{ borderRadius: "4px", minWidth: 40, height: 40 }}
      >
        <VisibilityIcon />
        <Typography variant="body1" sx={{ fontSize: 13, fontWeight: 600, ml: 1 }}>
          VISUALIZE
        </Typography>
      </IconButton>
    </>
  );
}

function CreationImage(props) {
  const {
    creation,
    size,
    fixedImageHeight,
    showThumbnail,
    showTools, // only used by tool page
    showFavoriteOverlay,
    showDownloadOverlay,
    lazy,
    indexToShow,
    noBackground,
    sx,
    disableBlur,
    forceBlur,
    responsive,
    onVisualizeClick,
  } = props;
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const auth = useAuth();

  const shouldUnblur =
    auth.isProUser || (auth.user && auth.user.credits > 5) || disableBlur;

  const showSingleImage = indexToShow !== undefined;
  const imagePaths = showSingleImage
    ? [creation.imagePaths[indexToShow]]
    : creation.imagePaths;

  let imgHeight = size === "small" && !showSingleImage ? 141 : 256;
  let imgWidth = imgHeight;
  if (fixedImageHeight && fixedImageHeight < imgHeight) {
    imgHeight = fixedImageHeight;
  } else if (showSingleImage && creation.bodyPart) {
    imgHeight = constants.BODY_PART_DESC[creation.bodyPart].height;
    imgWidth = constants.BODY_PART_DESC[creation.bodyPart].width;

    if (imgHeight > 650) {
      imgWidth = (imgWidth * 650) / imgHeight;
      imgHeight = 650;
    }
  }

  const blurAtIdx = 1;

  const onRemix = (idx) => {
    createEvent("remix", creation.id, idx);
    navigate("/", { state: { creationId: creation.id, idx } });
  };
  const [showPricingModal, setShowPricingModal] = useState(false);

  if (creation && creation.status === "SUCCESS" && imagePaths) {
    const body = (
      <>
        {showPricingModal && (
          <PricingModal
            modalClosedCallback={() => setShowPricingModal(false)}
            forceOpen
          />
        )}
        {imagePaths.map((imgPath, idx) => (
          <ImageListItem
            component="div"
            key={getImagePath(imgPath, showThumbnail, creation.style[0] === "upload")}
            sx={sx}
          >
            <img
              src={getImageUrl(
                getImagePath(imgPath, showThumbnail, creation.style[0] === "upload"),
                getCreationBucket(creation),
              )}
              alt={`${creation.prompt} tattoo design idea`}
              loading={lazy ? "lazy" : "eager"}
              style={{
                backgroundColor: "white",
                objectFit: "contain",
                height: fixedImageHeight,
                width: fixedImageHeight,
                filter:
                  `${noBackground ? "" : "brightness(0.95)"} ${
                    forceBlur ||
                    (!shouldUnblur && (indexToShow >= blurAtIdx || idx >= blurAtIdx))
                      ? "blur(0.25rem)"
                      : ""
                  }` || "none",
                borderRadius: 8,
                pointerEvents: "none",
                aspectRatio: `${imgWidth}/${imgHeight}`,
                maxWidth: "100%",
                maxHeight: imgHeight,
              }}
              width="100%"
              height="auto"
              draggable={false}
              // Use showTools to use strictly on detail pages for LCP purposes
              // eslint-disable-next-line react/no-unknown-property
              fetchpriority={showTools && idx === 0 ? "high" : "auto"}
            />
            {showTools && (shouldUnblur || idx < blurAtIdx) && (
              <Stack direction="row" justifyContent="space-between" mt={0.5}>
                <FavoriteButton creationId={creation.id} idx={idx} />
                <DownloadButton creation={creation} idx={idx} />
                <div style={{ flexGrow: 1 }} />
                <Button
                  color="primary"
                  startIcon={<ModelTrainingIcon />}
                  onClick={() => onRemix(idx)}
                >
                  Remix
                </Button>
              </Stack>
            )}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                position: "absolute",
                right: 10,
                top: 10,
              }}
            >
              {showDownloadOverlay && (
                <Box>
                  <DownloadButton creation={creation} idx={idx} variant="icon" />
                </Box>
              )}
              {showFavoriteOverlay && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  {onVisualizeClick && (
                    <VisualizationButton onVisualizeClick={onVisualizeClick} />
                  )}
                  {(auth.isProUser || (auth.user && auth.user.credits < 0)) && (
                    <FavoriteButton creationId={creation.id} idx={indexToShow} />
                  )}
                  {(auth.isUnauthed || (auth.user && auth.user.credits > -1)) && (
                    <DownloadIconButton
                      downloadClickCallback={() => setShowPricingModal(true)}
                    />
                  )}
                </Box>
              )}
            </Stack>

            {(!showThumbnail || showFavoriteOverlay) &&
              !shouldUnblur &&
              (indexToShow >= blurAtIdx || idx >= blurAtIdx) && (
                <Box
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.5)",
                    zIndex: 100,
                  }}
                >
                  <Button
                    component={Link}
                    variant="outlined"
                    color="primary"
                    to="/pricing"
                    onClick={() => {
                      analytics.track("cta-click", {
                        context: "image_detail",
                        action: auth.user ? "upgrade" : "join",
                        button_text: auth.user ? "unlock_pro" : "signup_upgrade_unlock",
                      });
                    }}
                    sx={{
                      lineHeight: 1.4,
                    }}
                  >
                    <ProBadge
                      color={theme.palette.primary.main}
                      height={30}
                      sx={{ ml: -0.5, mr: 1 }}
                    />
                    {auth.user ? "Unlock with Pro" : "Subscribe to Unlock"}
                  </Button>
                </Box>
              )}
            {showThumbnail &&
              !shouldUnblur &&
              (indexToShow >= blurAtIdx || idx >= blurAtIdx) && (
                <Box
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 100,
                  }}
                >
                  <Lock color="action" />
                </Box>
              )}
          </ImageListItem>
        ))}
      </>
    );

    if (showSingleImage) {
      return body;
    }

    return (
      <ImageList
        sx={{
          width: "100%",
          margin: 0,
        }}
        gap={0}
        cols={(responsive && matchDownMd) || creation.params?.width > 512 ? 1 : 2}
      >
        {body}
      </ImageList>
    );
  }

  return (
    <Skeleton
      variant="rectangular"
      height={0}
      sx={{
        paddingBottom: "100%",
        maxWidth: "100%",
      }}
      animation={creation ? "pulse" : false}
    />
  );
}

export default CreationImage;
